import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'
import type { IAuditCalendarStoreState } from '@/modules/audits/stores/useAuditCalendarStore/types/IAuditCalendarStoreState'
import type { IAuditEvent } from '@/modules/audits/stores/useAuditCalendarStore/types/IAuditEvent'
import type { IAuditType } from '@/modules/audits/stores/useAuditCalendarStore/types/IAuditType'

export const AUDIT_OVERDUE_TYPE_ID = 'overdue'

const OVERDUE_TYPE: IAuditType = {
  id: AUDIT_OVERDUE_TYPE_ID,
  title: 'Просроченные',
}

export const useAuditCalendarStore = defineStore('auditCalendar', {
  state: (): IAuditCalendarStoreState => ({
    events: [],
    resources: [],
    auditTypes: [],
    selectedAuditTypes: [],
  }),

  actions: {
    async fetchAuditCalendarEvents(startDate: string, endDate: string, resource?: 'departments') {
      if (this.selectedAuditTypes.length === 0) {
        this.events = []
        return
      }

      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = {
          calendars: 'Аудиты',
          start_date: startDate,
          end_date: endDate,
        }

        if (resource) params.resource = resource

        const selectedTypes = this.selectedAuditTypes.filter(id => id !== AUDIT_OVERDUE_TYPE_ID)
        const overdueSelected = this.selectedAuditTypes.includes(AUDIT_OVERDUE_TYPE_ID)
        const allTypeIds = this.auditTypes
          .filter(t => t.id !== AUDIT_OVERDUE_TYPE_ID)
          .map(t => t.id)
        const isAllSelected = this.selectedAuditTypes.length === this.auditTypes.length

        /*
         * Полный набор — без сужения на API.
         * Только типы — audit_type.
         * Только просроченные — is_overdue.
         * Типы + просроченные (не все) — без audit_type, фильтр на клиенте (OR).
         */
        if (!isAllSelected) {
          if (selectedTypes.length === 0 && overdueSelected) {
            params.is_overdue = true
          }
          else if (selectedTypes.length > 0 && selectedTypes.length < allTypeIds.length && !overdueSelected) {
            params.audit_type = selectedTypes.join(',')
          }
          else if (selectedTypes.length > 0 && selectedTypes.length < allTypeIds.length && overdueSelected) {
            // широкий ответ, ниже отфильтруем по type OR overdue
          }
          else if (selectedTypes.length === allTypeIds.length && !overdueSelected) {
            // все виды кроме просроченных — забираем всё и режем is_overdue на клиенте
          }
        }

        const response = await axios.get(onboardingApiUrl('audit', '/calendar/events/'), { params })
        const results: IAuditEvent[] = response.data.results ?? []

        if (isAllSelected) {
          this.events = results
        }
        else {
          this.events = results.filter(event => {
            const typeMatch = selectedTypes.includes(event.extendedProps?.audit_type)
            const overdueMatch = overdueSelected && Boolean(event.extendedProps?.is_overdue)

            return typeMatch || overdueMatch
          })
        }
      } catch (error) {
        console.error('Ошибка загрузки событий аудита:', error)
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async fetchAuditTypes() {
      try {
        /*
         * Учебная ручка resources всегда отдаёт отделения и не смотрит на resource=audit_type.
         * Виды в сайдбаре остаются с боевой ручки.
         */
        const response = await axios.get(`${BASE_URL}/api/calendar/events/resources/`, {
          params: { resource: 'audit_type' },
        })
        const fromApi: IAuditType[] = response.data.results ?? []
        const withoutOverdue = fromApi.filter(t => t.id !== AUDIT_OVERDUE_TYPE_ID)

        this.auditTypes = [...withoutOverdue, OVERDUE_TYPE]
        this.selectedAuditTypes = this.auditTypes.map(t => t.id)
      } catch (error) {
        console.error('Ошибка загрузки типов аудитов:', error)
      }
    },

    async fetchResources() {
      try {
        const response = await axios.get(
          onboardingApiUrl('audit', '/calendar/events/resources/'),
          {
            params: {
              resource: 'departments',
            },
          },
        )

        this.resources = response.data.results
      } catch (error) {
        console.error('Ошибка загрузки ресурсов:', error)
      }
    },

    // Заглушки CRUD (если понадобятся)
    async addEvent(event: any) {},
    async updateEvent(event: any) {},
    async removeEvent(eventId: string) {},
  },
})
